import AddIcon from "@mui/icons-material/Add";
import { Box } from "@mui/material";
import Fab from "@mui/material/Fab";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getArts } from "../../../actions/arts";
import { impersonateUser } from "../../../actions/login";
import { adminChangePassword } from "../../../actions/password";
import { getProviders } from "../../../actions/providers";
import {
  getListUsers,
  getUserRoles,
  toggleMFA,
  updateMfaUserStatus,
  userDelete,
  userEdit,
  userRegister,
} from "../../../actions/users";
import AdminChangePasswordDialog from "../../commons/dialog/adminChangePasswordDialog";
import ConfirmDeleteDialog from "../../commons/dialog/confirmDeleteDialog";
import ConfirmDialog from "../../commons/dialog/confirmDialog";
import EditUserDialog from "../../commons/dialog/editUserDialog";
import useDialog from "../../commons/hooks/useDialog";
import UsersTable from "../../commons/table/usersTable";

const initialUser = {
  nombre: "",
  apellido: "",
  email: "",
  idRol: "",
  idArt: "",
  prestadores: [],
  referenteGestor: null,
};

const UsersContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const users = useSelector((state) => state.users.listUsers);
  const adminChangePasswordLoading = useSelector(
    (state) => state.password.adminChangePassword.isFetching
  );
  const loadingEditUser = useSelector((state) => state.users.listUsers.isFetching);
  const loadingToggleMfa = useSelector((state) => state.users.toggleMFA.isFetching);
  const loadingArts = useSelector((state) => state.arts.isFetching);
  const loadingProviders = useSelector((state) => state.providers.listProviders.isFetching);
  // Ahora la mesa de ayuda cuenta con un usuario administrador
  // de tipo "Soporte". Su única funcionalidad habilitada
  // es impersonar usuarios.
  const esSoporte = useSelector(
    (state) => state.auth.roles?.tipo === "Soporte",
  );

  const changePasswordDialog = useDialog(null);
  const confirmChangePasswordDialog = useDialog(null);
  const editDialog = useDialog(initialUser);
  const deleteDialog = useDialog(null);
  const confirmEditDialog = useDialog(null);
  const mfaDialog = useDialog(null);

  const addingNewUser = !editDialog.data.id;

  useEffect(() => {
    dispatch(getListUsers());
  }, [dispatch]);

  const handleSubmitUser = (userData, isNew) => {
    if (isNew) {
      dispatch(userRegister(userData)).then(() => {
        dispatch(getListUsers());
        enqueueSnackbar(<p>Se ha creado el usuario correctamente!</p>, {
          variant: "success",
        });
      });
      editDialog.close();
    } else {
      confirmEditDialog.open(userData);
    }
  };

  const handleConfirmEdit = () => {
    if (confirmEditDialog.data) {
      dispatch(userEdit(confirmEditDialog.data)).then(() => {
        dispatch(getListUsers());
        enqueueSnackbar(<p>¡Se ha editado el usuario correctamente!</p>, {
          variant: "success",
        });
        confirmEditDialog.close();
        editDialog.close();
      });
    }
  };

  const handleDelete = () => {
    if (deleteDialog.data) {
      dispatch(userDelete(deleteDialog.data)).then(() =>
        enqueueSnackbar(<p>Se ha eliminado el usuario correctamente!</p>, {
          variant: "success",
        }),
      );
      deleteDialog.close();
    }
  };

  const handleImpersonate = (user) => {
    dispatch(impersonateUser(user)).then(() => navigate("/home"));
  };

  const handleOpenEdit = (userData = null) => {
    dispatch(getUserRoles());
    // MLP-1413 Para que prestadores y arts ya estén cargados al abrir el dialog.
    dispatch(getArts());
    dispatch(getProviders());

    if (userData) {
      const idArt = userData.arts?.length > 0 ? userData.arts[0].id : "";
      const prestadoresIds = userData.prestadores?.map(p => p.id) || [];
      const userForm = {
        nombre: userData.nombre || "",
        apellido: userData.apellido || "",
        email: userData.email || "",
        idRol: userData.idRol || "",
        idArt,
        prestadores: prestadoresIds,
        referenteGestor: userData.referenteGestor ?? null,
        id: userData.id,
      };
      editDialog.open(userForm);
    } else {
      editDialog.open(initialUser);
    }
  };

  const handleToggleMFA = () => {
    if (mfaDialog.data) {
      const user = mfaDialog.data;
      dispatch(toggleMFA(user.id, !user.twoFactorEnabled)).then(() => {
        dispatch(updateMfaUserStatus(user.id, !user.twoFactorEnabled));
        enqueueSnackbar(<p>El MFA ha sido {user.twoFactorEnabled ? "activado" : "desactivado"} correctamente</p>, {
          variant: "success",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        });
      });
      mfaDialog.close();
    }
  };

  const handleConfirmChangePassword = () => {
    dispatch(adminChangePassword(changePasswordDialog.data.user.id, confirmChangePasswordDialog.data))
      .then(() => {
        enqueueSnackbar(<p>Contraseña reseteada correctamente.</p>, {
          variant: "success",
        });
        confirmChangePasswordDialog.close();
        changePasswordDialog.close();
      })
      .catch(() => {
        confirmChangePasswordDialog.close();
      });
  };

  return (
    <div>
      <UsersTable
        data={users.list}
        loading={loadingEditUser}
        title="Usuarios"
        fetchData={() => dispatch(getListUsers())}
        handleOpenDelete={(user) => deleteDialog.open(user)}
        handleImpersonate={handleImpersonate}
        handleOpenChangePassword={(user) => changePasswordDialog.open({ user })}
        handleOpenEdit={!esSoporte && handleOpenEdit}
        handleChangeMfa={(user) => mfaDialog.open(user)}
      />

      {!esSoporte && (
        <Box
          sx={{
            margin: "5px 20px",
            display: "flex",
            justifyContent: "flex-end",
          }}>
          <Fab
            onClick={() => handleOpenEdit(null)}
            disabled={loadingEditUser}
            color="primary"
            aria-label="add">
            <AddIcon />
          </Fab>
        </Box>
      )}

      <EditUserDialog
        user={editDialog.data}
        open={editDialog.isOpen}
        handleClose={editDialog.close}
        addingNewUser={addingNewUser}
        handleSubmit={handleSubmitUser}
        availableRoles={["Prestador administrativo", "Prestador de traslado", "Agente de Traslados", "Gestor de Siniestros", "Soporte", "Supervisor Art", "Usuario Art"]}
        loading={loadingArts || loadingProviders}
      />

      <ConfirmDeleteDialog
        open={deleteDialog.isOpen}
        handleClose={deleteDialog.close}
        handleConfirm={handleDelete}
        title="Eliminar Usuario"
        text={`¿Está seguro que desea eliminar el usuario ${deleteDialog.data?.email}?`}
      />

      <ConfirmDialog
        open={mfaDialog.isOpen}
        handleClose={mfaDialog.close}
        handleConfirm={handleToggleMFA}
        title="Cambiar MFA"
        text={`¿Está seguro de que desea ${mfaDialog.data?.twoFactorEnabled ? "desactivar" : "activar"} el MFA para el usuario ${mfaDialog.data?.email}?`}
        loading={loadingToggleMfa}
      />

      <ConfirmDialog
        open={confirmEditDialog.isOpen}
        handleClose={confirmEditDialog.close}
        handleConfirm={handleConfirmEdit}
        title="Confirmar edición"
        text="¿Está seguro de que desea modificar el usuario?"
        loading={loadingEditUser}
      />

      <AdminChangePasswordDialog
        open={changePasswordDialog.isOpen}
        onClose={changePasswordDialog.close}
        onSubmit={(password) => confirmChangePasswordDialog.open(password)}
        loading={adminChangePasswordLoading}
      />

      <ConfirmDialog
        open={confirmChangePasswordDialog.isOpen}
        handleClose={confirmChangePasswordDialog.close}
        handleConfirm={handleConfirmChangePassword}
        title="Actualizar Contraseña"
        text={`¿Está seguro que desea actualizar la contraseña del usuario ${changePasswordDialog.data?.user.email}?`}
        loading={adminChangePasswordLoading}
      />
    </div>
  );
};

export default UsersContainer;
